import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getMyWork, getPeople, rankItem, setDue, setInCycle, setPriority, setStatus, startCycle } from '../lib/api';
import type { MyWorkItem, Person } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsync } from '../lib/useAsync';
import { GROUP_LABEL, boardGroup } from '../lib/format';
import { Chip, DueChip, KindChip, PriorityChip } from '../components/Chips';

// The sections a person works down, top to bottom. The server (tracker.v_my_work)
// decides each item's section and the order; this page only groups the list.
const SECTIONS: { id: MyWorkItem['section']; label: string; hint: string; folded?: boolean }[] = [
  { id: 'urgent', label: 'Urgent', hint: 'p0 or past the due date' },
  { id: 'cycle', label: 'This cycle', hint: 'in your order; move with the arrows' },
  { id: 'untriaged', label: 'Untriaged', hint: 'no priority yet: decide p0 to p3' },
  { id: 'backlog', label: 'Backlog', hint: 'by priority' },
  { id: 'waiting', label: 'Waiting', hint: 'built or shipped, to test or validate', folded: true },
  { id: 'hold', label: 'On hold', hint: 'parked', folded: true },
];

const ACTIVE = new Set(['open', 'planned', 'in_progress']);

// nextMonday as YYYY-MM-DD: today when it is Monday, else the coming one.
function nextMonday(): string {
  const d = new Date();
  d.setDate(d.getDate() + ((8 - d.getDay()) % 7));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function MyWorkPage() {
  const { email, role } = useAuth();
  const [params, setParams] = useSearchParams();
  const who = params.get('who') || '';
  const { data, error, loading, reload } = useAsync(() => getMyWork(who || undefined), [who]);
  const [people, setPeople] = useState<Person[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cycleStart, setCycleStart] = useState(nextMonday());

  useEffect(() => {
    getPeople().then((p) => setPeople(p.people)).catch(() => setPeople([]));
  }, []);

  const items = data?.items ?? [];
  const sections = useMemo(
    () => SECTIONS.map((s) => ({ ...s, items: items.filter((i) => i.section === s.id) })).filter((s) => s.items.length),
    [items],
  );
  const mine = !who || data?.owner === email;
  const ownerName = people.find((p) => p.email === data?.owner)?.name ?? data?.owner ?? '';
  const cycle = data?.cycle ?? null;

  // act runs a write and reloads, because order and sections come from the server
  async function act(label: string, fn: () => Promise<unknown>) {
    setActionError(null);
    setBusy(true);
    try {
      await fn();
      reload();
    } catch (e) {
      setActionError(`${label}: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="detail-head">
        <h1>{mine ? 'My work' : `${ownerName}'s work`}</h1>
        <span className="count">{items.length} assigned</span>
        <select
          style={{ width: 'auto', marginLeft: 'auto' }}
          value={who}
          onChange={(e) => setParams(e.target.value ? { who: e.target.value } : {})}
          title="Whose work"
        >
          <option value="">Me</option>
          {people
            .filter((p) => p.email !== email)
            .map((p) => (
              <option key={p.email} value={p.email}>
                {p.name}
              </option>
            ))}
        </select>
      </div>

      {data ? (
        cycle ? (
          <p className="hint">
            {cycle.title}: {cycle.starts_on} to {cycle.target_date}. p1 means in this cycle.
          </p>
        ) : (
          <div className="row hint">
            <span>No cycle is running.</span>
            {role === 'developer' ? (
              <>
                <input type="date" value={cycleStart} onChange={(e) => setCycleStart(e.target.value)} title="First day" />
                <button className="ghost" disabled={busy} onClick={() => act('start cycle', () => startCycle(cycleStart, 2))}>
                  Start a two-week cycle
                </button>
              </>
            ) : null}
          </div>
        )
      ) : null}

      {loading && !items.length ? <p className="loading">Loading...</p> : null}
      {error ? <div className="error">{error}</div> : null}
      {actionError ? <div className="error">{actionError}</div> : null}
      {data?.warnings?.length ? (
        <div className="error">
          {data.warnings.map((w) => (
            <div key={w}>{w}</div>
          ))}
        </div>
      ) : null}
      {data && !items.length ? (
        <p className="empty">Nothing assigned{mine ? ' to you' : ''}. Assign work from the board or an item page.</p>
      ) : null}

      {sections.map((s) => {
        const rows = s.items.map((item, n) => (
          <div key={item.id} className="mywork-row">
            <div>
              <Link to={`/item/${item.key}`} className="key">
                {s.id === 'cycle' ? `${n + 1}. ` : ''}
                {item.key}
              </Link>
              <Link to={`/item/${item.key}`} className="title">
                {item.title}
              </Link>
              <div className="meta">
                <Chip>{GROUP_LABEL[boardGroup(item)] ?? item.status}</Chip>
                <KindChip kind={item.kind} />
                <PriorityChip priority={item.priority} />
                <DueChip due={item.due_date} />
                {item.in_cycle && s.id !== 'cycle' ? <Chip>in cycle</Chip> : null}
                {data?.openTasks[item.id] ? <span className="left">{data.openTasks[item.id]} left</span> : null}
              </div>
            </div>
            <div className="row" style={{ gap: 8 }}>
              {s.id === 'cycle' ? (
                <>
                  <button className="ghost" title="Earlier" disabled={busy || n === 0} onClick={() => act(item.key, () => rankItem(item.key, n))}>
                    ↑
                  </button>
                  <button
                    className="ghost"
                    title="Later"
                    disabled={busy || n === s.items.length - 1}
                    onClick={() => act(item.key, () => rankItem(item.key, n + 2))}
                  >
                    ↓
                  </button>
                  <button className="ghost" disabled={busy} onClick={() => act(item.key, () => setInCycle(item.key, false))}>
                    Out of cycle
                  </button>
                </>
              ) : cycle && ACTIVE.has(item.status) && !item.in_cycle ? (
                <button className="ghost" disabled={busy} onClick={() => act(item.key, () => setInCycle(item.key, true))}>
                  Add to cycle
                </button>
              ) : null}
              {role === 'developer' && ACTIVE.has(item.status) ? (
                <select
                  style={{ width: 'auto' }}
                  value={item.priority ?? ''}
                  title="Priority: p0 room or visitors affected now, p1 this cycle, p2 backlog, p3 ideas"
                  disabled={busy}
                  onChange={(e) => act(item.key, () => setPriority(item.key, e.target.value || null))}
                >
                  <option value="">untriaged</option>
                  <option value="p0">p0 now</option>
                  <option value="p1">p1 cycle</option>
                  <option value="p2">p2 backlog</option>
                  <option value="p3">p3 idea</option>
                </select>
              ) : null}
              <input
                type="date"
                value={item.due_date ?? ''}
                title="Due date"
                onChange={(e) => act(item.key, () => setDue(item.key, e.target.value || null))}
              />
              {role === 'developer' && mine && (item.status === 'open' || item.status === 'planned') ? (
                <button className="ghost" disabled={busy} onClick={() => act(item.key, () => setStatus(item.key, 'in_progress'))}>
                  Start
                </button>
              ) : null}
            </div>
          </div>
        ));
        const head = (
          <>
            {s.label} <span className="count">{s.items.length}</span>{' '}
            <span className="col-hint" style={{ textTransform: 'none', letterSpacing: 0 }}>
              {s.hint}
            </span>
          </>
        );
        return (
          <section key={s.id} className="mywork-section">
            {s.folded ? (
              <details>
                <summary>
                  <h2 style={{ display: 'inline' }}>{head}</h2>
                </summary>
                <div className="panel">{rows}</div>
              </details>
            ) : (
              <>
                <h2>{head}</h2>
                <div className="panel">{rows}</div>
              </>
            )}
          </section>
        );
      })}

      <p className="hint" style={{ marginTop: 18 }}>
        p0 a room or visitors are affected now; p1 this cycle (about 5); p2 backlog; p3 ideas. New work starts untriaged.
        Anyone signed in can assign work, set a due date, put it in the cycle and order it; Claude can too (assign_item,
        set_due_date, set_cycle, rank_item).
      </p>
      {loading ? null : (
        <button className="ghost" onClick={reload}>
          Refresh
        </button>
      )}
    </>
  );
}
